import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  getWatchBrands,
  getExistingReferenceMapById,
  enrichReferenceWithAI,
  siblingReferencesFrom,
  isDescriptionRewriteCandidate as isCandidate,
  buildDescriptionEnrichSnapshot as buildSnapshot,
} from '../../services/firebase';
import type { WatchBrand, WatchReference } from '../../types';
import { Button } from '../common/Button';
import { BrandFilterSelect } from './BrandFilterSelect';

const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';

// Bounded parallelism — keeps us within the enrichReference Cloud Function /
// Anthropic rate limits while still finishing a brand quickly.
const CONCURRENCY = 4;

type ItemStatus = 'queued' | 'running' | 'updated' | 'failed';

interface RunItem {
  ref: WatchReference;
  status: ItemStatus;
  error?: string;
}

// `isCandidate` and `buildSnapshot` (and the snapshot denylist) live in the
// firebase service so this tool and the per-brand Enrichment Tasks panel share
// one source of truth — imported above under their local aliases.

export function BulkDescriptionRegen() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  // Override: when true, every ref in the brand is eligible (the isCandidate
  // skip filter is bypassed). Used for cleanup re-runs after a prompt change.
  const [override, setOverride] = useState(false);

  const [scanning, setScanning] = useState(false);
  // null = not yet scanned for the current brand.
  const [scan, setScan] = useState<{
    brandId: string;
    brandName: string;
    totalRefs: number;
    candidates: WatchReference[];
    // All refs in the brand — used to compute per-ref sibling references.
    allRefs: WatchReference[];
  } | null>(null);

  const [running, setRunning] = useState(false);
  const [items, setItems] = useState<RunItem[]>([]);
  const [done, setDone] = useState<{ updated: number; failed: number; stopped: boolean } | null>(
    null,
  );
  const stopRef = useRef(false);

  // Load brands once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) setBrands(list.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) || null,
    [brands, selectedBrandId],
  );

  // Reset scan/run state whenever the brand OR the override toggle changes — a
  // stale candidate count would otherwise misrepresent what Run will process.
  useEffect(() => {
    setScan(null);
    setItems([]);
    setDone(null);
  }, [selectedBrandId, override]);

  const handleFindCandidates = useCallback(async () => {
    if (!selectedBrand) return;
    setScanning(true);
    setScan(null);
    setItems([]);
    setDone(null);
    try {
      // Doc-id-keyed: a ref-string-keyed map collapses docs sharing a
      // `reference`, silently dropping one of every such pair from both the
      // candidate list and the sibling ground truth.
      const map = await getExistingReferenceMapById(selectedBrand.id);
      const allRefs = Array.from(map.values());
      // Override bypasses the skip filter — every ref in the brand is eligible.
      // Variants (parentReferenceId set) are NEVER eligible, override or not:
      // they're not consumer-facing (Algolia + RN Explore skip them), so
      // AI-rewriting their descriptions wastes tokens and risks variant-vs-parent
      // drift. Parents-only, matching the server-side backfillAIDescriptions rule.
      const candidates = allRefs
        .filter((ref) => !ref.parentReferenceId && (override || isCandidate(ref)))
        .sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
      setScan({
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        totalRefs: allRefs.length,
        candidates,
        allRefs,
      });
    } catch (err) {
      console.error('Failed to scan references', err);
      toast.error('Failed to scan references');
    } finally {
      setScanning(false);
    }
  }, [selectedBrand, override]);

  const updateItem = useCallback((index: number, update: Partial<RunItem>) => {
    setItems((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], ...update };
      return next;
    });
  }, []);

  const handleRun = useCallback(async () => {
    if (!scan || scan.candidates.length === 0) return;
    const { brandId, brandName, candidates, allRefs } = scan;

    stopRef.current = false;
    setRunning(true);
    setDone(null);
    setItems(candidates.map((ref) => ({ ref, status: 'queued' as ItemStatus })));

    let updated = 0;
    let failed = 0;
    let cursor = 0;

    const worker = async () => {
      // Single-threaded JS: reading + incrementing `cursor` between awaits is
      // safe (no true concurrency), so each worker claims a distinct index.
      while (true) {
        if (stopRef.current) return;
        const i = cursor;
        cursor += 1;
        if (i >= candidates.length) return;

        const ref = candidates[i];
        updateItem(i, { status: 'running' });

        // Idempotency short-circuit (defensive — candidates already exclude
        // these). Skipped under override, where re-running AI-rewritten refs is
        // the whole point.
        if (!override && ref.descriptionSource === 'ai') {
          updated += 0;
          failed += 1;
          updateItem(i, { status: 'failed', error: 'Already AI-rewritten (skipped)' });
          continue;
        }

        try {
          // Other refs in this ref's collection, as ground truth for "Notable Variants".
          const siblingReferences = siblingReferencesFrom(allRefs, ref);
          const snapshot = buildSnapshot(ref, brandName, siblingReferences);
          const result = await enrichReferenceWithAI(snapshot);
          const raw = result.suggestions?.description;
          const newDesc = typeof raw === 'string' ? raw.trim() : '';
          if (!newDesc) {
            throw new Error('AI returned no description');
          }
          await updateDoc(
            doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, ref.id),
            {
              description: newDesc,
              // Fill-empty, write-once: snapshot the pre-AI description the first
              // time we overwrite it, so future enrichment keeps the source text.
              ...(ref.originalDescription || !ref.description
                ? {}
                : { originalDescription: ref.description }),
              descriptionSource: 'ai',
              descriptionAiRewrittenAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
          );
          updated += 1;
          updateItem(i, { status: 'updated' });
        } catch (err) {
          failed += 1;
          const msg = err instanceof Error ? err.message : String(err);
          console.error(`Description rewrite failed for ${ref.reference}:`, err);
          updateItem(i, { status: 'failed', error: msg });
        }
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    const stopped = stopRef.current;
    setRunning(false);
    setDone({ updated, failed, stopped });

    // Drop successfully-rewritten refs from the in-memory candidate list so a
    // re-run (or Find again) reflects the new state without a reload.
    setScan((prev) =>
      prev
        ? { ...prev, candidates: prev.candidates.filter((c) => c.descriptionSource !== 'ai') }
        : prev,
    );

    if (stopped) {
      toast(`Stopped — ${updated} updated, ${failed} failed`);
    } else if (failed === 0) {
      toast.success(`Done — ${updated} reference${updated === 1 ? '' : 's'} updated`);
    } else {
      toast.error(`Done — ${updated} updated, ${failed} failed`);
    }
  }, [scan, updateItem, override]);

  const handleStop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const processedCount = useMemo(
    () => items.filter((it) => it.status === 'updated' || it.status === 'failed').length,
    [items],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">AI Description Rewrite</h2>
        <p className="mt-1 text-sm text-gray-600">
          Find references where the description hasn't been AI-rewritten yet (either raw imported
          text or missing entirely), then run the AI description rule on each to produce a clean,
          consistent description. References already marked AI-rewritten (or hand-edited) are
          skipped.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-md">
        <BrandFilterSelect
          brands={brands}
          loading={loadingBrands}
          value={selectedBrandId}
          onChange={setSelectedBrandId}
        />
        <div className="mt-3">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
              disabled={scanning || running}
            />
            <span>Override — re-run on ALL refs (ignore skip filter)</span>
          </label>
          {override && (
            <p className="mt-1 ml-6 text-xs text-amber-700">
              ⚠ This will overwrite descriptions that have already been AI-rewritten or
              hand-edited. Use for cleanup runs after prompt changes.
            </p>
          )}
        </div>
        <div className="mt-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleFindCandidates}
            disabled={!selectedBrandId || scanning || running}
            loading={scanning}
          >
            Find candidates
          </Button>
        </div>
      </div>

      {scan && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-gray-900">
                <span className="font-semibold">{scan.candidates.length}</span> ref
                {scan.candidates.length === 1 ? '' : 's'} eligible for AI description rewrite
                <span className="text-gray-500">
                  {' '}
                  (of {scan.totalRefs} in {scan.brandName})
                </span>
              </p>
              {running && (
                <p className="mt-1 text-xs text-gray-500">
                  {processedCount} / {items.length} processed
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!running && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleRun}
                  disabled={scan.candidates.length === 0}
                >
                  Run AI on {scan.candidates.length} ref{scan.candidates.length === 1 ? '' : 's'}
                </Button>
              )}
              {running && (
                <Button variant="danger" size="sm" onClick={handleStop}>
                  Stop
                </Button>
              )}
            </div>
          </div>

          {done && (
            <div
              className={`rounded-md px-3 py-2 text-sm ${
                done.failed === 0 && !done.stopped
                  ? 'bg-green-50 text-green-800'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {done.stopped ? 'Stopped. ' : 'Complete. '}
              {done.updated} updated, {done.failed} failed.
            </div>
          )}

          {items.length > 0 && (
            <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
              {items.map((it, idx) => (
                <div
                  key={it.ref.id || idx}
                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                >
                  <span className="font-mono text-gray-700 truncate">
                    {it.ref.reference || it.ref.id}
                    {it.ref.model && (
                      <span className="ml-2 font-sans text-gray-400">{it.ref.model}</span>
                    )}
                  </span>
                  <StatusBadge status={it.status} error={it.error} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, error }: { status: ItemStatus; error?: string }) {
  switch (status) {
    case 'queued':
      return <span className="text-gray-400 shrink-0">Queued</span>;
    case 'running':
      return <span className="text-blue-600 shrink-0">Running…</span>;
    case 'updated':
      return <span className="text-green-600 shrink-0">✓ Updated</span>;
    case 'failed':
      return (
        <span className="text-red-600 shrink-0" title={error}>
          ✗ {error ? error.slice(0, 40) : 'Failed'}
        </span>
      );
  }
}
